import { AxiosInstance } from "axios";
import { getMetaAttributionValues } from "@/common/services/analytics/attribution";
import { trackInitiateCheckout } from "@/common/services/analytics";

const CHECKOUT_API_PREFIX = "/payments/checkout";

export type CheckoutTrackingPayload = {
	value?: number;
	currency?: string;
	contentName?: string;
	contentIds?: string[];
	numItems?: number;
};

type CheckoutKind = "entry" | "plan" | "merch";

type MetaTrackingFields = {
	metaEventId: string;
	fbc?: string;
	fbp?: string;
	metaValue?: number;
	metaCurrency: string;
	metaContentName: string;
	metaContentIds: string[];
	metaNumItems?: number;
};

/**
 * Fires the Meta InitiateCheckout pixel event and returns the snake_cased
 * meta_* fields every backend payment-intent endpoint expects on its body.
 * Centralizes the meta_event_id generation + attribution lookup so individual
 * service fns stay short and consistent.
 */
function buildCheckoutTracking(
	kind: CheckoutKind,
	tracking: CheckoutTrackingPayload | undefined,
	defaultContentIds: string[]
): MetaTrackingFields {
	const metaEventId = `init_checkout_${kind}_${Date.now()}_${Math.random()
		.toString(36)
		.slice(2, 10)}`;
	const { fbc, fbp } = getMetaAttributionValues();
	const contentName = tracking?.contentName ?? `${kind}_checkout`;
	const contentIds = tracking?.contentIds ?? defaultContentIds;
	const currency = tracking?.currency ?? "AUD";

	trackInitiateCheckout(
		{
			content_type: "product",
			content_name: contentName,
			content_ids: contentIds,
			currency,
			value: tracking?.value,
			num_items: tracking?.numItems,
		},
		{ eventID: metaEventId }
	);

	return {
		metaEventId,
		fbc,
		fbp,
		metaValue: tracking?.value,
		metaCurrency: currency,
		metaContentName: contentName,
		metaContentIds: contentIds,
		metaNumItems: tracking?.numItems,
	};
}

export const createEntryPaymentIntent = async (
	http: AxiosInstance,
	pageUrl: string,
	userId: number,
	priceId: string,
	entryId: number,
	tracking?: CheckoutTrackingPayload
): Promise<{ clientSecret: string; paymentIntentId: string }> => {
	const meta = buildCheckoutTracking("entry", tracking, [`entry_${entryId}`]);

	const response = await http.post<{
		clientSecret: string;
		paymentIntentId: string;
	}>(`${CHECKOUT_API_PREFIX}/create-entry-payment-intent`, {
		userId,
		pageUrl,
		priceId,
		entryId,
		...meta,
	});

	return response.data;
};

export type SubscriptionPaymentIntentResponse = {
	clientSecret: string;
	subscriptionId: string;
	subtotal: number;
	discountAmount: number;
	total: number;
	promotionCodeApplied: string | null;
};

export const createSubscriptionPaymentIntent = async (
	http: AxiosInstance,
	pageUrl: string,
	userId: number,
	priceId: string,
	planId: number,
	tracking?: CheckoutTrackingPayload,
	promotionCode?: string
): Promise<SubscriptionPaymentIntentResponse> => {
	const meta = buildCheckoutTracking("plan", tracking, [`plan_${planId}`]);

	const response = await http.post<SubscriptionPaymentIntentResponse>(
		`${CHECKOUT_API_PREFIX}/create-subscription-payment-intent`,
		{
			pageUrl,
			userId,
			priceId,
			planId,
			promotionCode,
			...meta,
		}
	);

	return response.data;
};

export type MerchPaymentIntentResponse = {
	clientSecret: string;
	paymentIntentId: string;
	subtotal: number;
	shippingFee: number;
	discountAmount: number;
	total: number;
	currency: string;
	promotionCodeApplied: string | null;
};

export const createMerchPaymentIntent = async (
	http: AxiosInstance,
	pageUrl: string,
	tracking?: CheckoutTrackingPayload,
	promotionCode?: string
): Promise<MerchPaymentIntentResponse> => {
	const meta = buildCheckoutTracking("merch", tracking, ["merch_cart"]);

	const response = await http.post<MerchPaymentIntentResponse>(
		`${CHECKOUT_API_PREFIX}/create-merch-payment-intent`,
		{
			pageUrl,
			promotionCode,
			...meta,
		}
	);

	return response.data;
};
